import * as React from 'react'
import { Link } from 'react-router-dom'
import { GRAPH_NODE_TYPE_REGISTRY, humanizeUrn, type NodeLabel } from '@atbash-labs/ontology-core'
import { graphHighlightHref } from '@atbash-labs/plantview-links'
import {
  collectAdditionalProperties,
  normalizeProcedureSteps,
  useGetPlantProcedureDetailQuery,
  usePlantSessionPaint,
  type PlantLine,
  type PlantProcedureAppliesTo,
  type PlantProcedureDetail,
  type PlantProcedureDetailNode,
  type PlantProcedurePartRequirement,
  type PlantProcedureSkillRequirement,
  type PlantProductRef,
  type PlantSite
} from '@axilon-apps/domains-ontology-ui-state'
import {
  SourceDocChip,
  SourceDocViewer,
  useSourceDocViewer
} from '../../../components/SourceDocViewer'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { plantEquipmentIndexHref } from '../../../lib/plant-links'
import { formatGradeRange } from '../labor-format'
import { PlantHref } from '../PlantHref'
import { registryIconFor } from '../registry-icons'
import {
  SessionBreadcrumbCurrent,
  SessionEntityMark,
  SessionTitleMark,
  sessionDetailHeaderClass
} from '../session-mark-ui'
import { EntityDetailsInspector } from './EntityDetailsInspector'
import { PlantStrayPill } from './PlantStrayPill'
import { registeredLabel } from './entity-surface'
import { procedureViewerSourceFile, procedureViewerSourcePage } from './procedure-source'
import { SectionHeader } from './SectionHeader'

function peerDisplayName(peer: PlantProcedureAppliesTo['peer'], labels: string[]): string {
  const label = registeredLabel(labels)
  const field = label ? GRAPH_NODE_TYPE_REGISTRY[label].displayLabelField : 'name'
  const value = peer[field]
  if (typeof value === 'string' && value.trim()) return value
  if (peer.name !== peer.id) return peer.name
  return humanizeUrn(peer.id)
}

function groupAppliesTo(
  rows: PlantProcedureAppliesTo[]
): Array<{ label: NodeLabel | null; rows: PlantProcedureAppliesTo[] }> {
  const order: string[] = []
  const byKey = new Map<string, PlantProcedureAppliesTo[]>()
  for (const row of rows) {
    const label = registeredLabel(row.peerLabels)
    const key = label ?? '__unregistered__'
    if (!byKey.has(key)) {
      order.push(key)
      byKey.set(key, [])
    }
    byKey.get(key)!.push(row)
  }
  return order.map((key) => ({
    label: key === '__unregistered__' ? null : (key as NodeLabel),
    rows: byKey.get(key)!
  }))
}

export function ProcedureEntityPage({
  entityId
}: {
  entityId: string
  label: NodeLabel
}): React.ReactElement {
  const { data, isLoading, isError } = useGetPlantProcedureDetailQuery(entityId, {
    skip: !entityId
  })
  const { markFor } = usePlantSessionPaint()

  if (isLoading) {
    return (
      <div className="flex min-w-0 flex-col gap-card">
        <ProcedureFactoryBreadcrumb />
        <div className="rounded-xl border bg-card p-8 text-sm text-ink-3">Loading procedure…</div>
      </div>
    )
  }
  if (isError || !data) {
    return (
      <div className="flex min-w-0 flex-col gap-card">
        <ProcedureFactoryBreadcrumb />
        <div className="rounded-xl border bg-card p-8 text-card-foreground shadow-sm">
          <h1 className="m-0 text-xl font-semibold">Procedure not found</h1>
          <p className="mt-2 break-all font-mono text-xs text-ink-3">{entityId}</p>
          <Button asChild variant="outline" className="mt-5">
            <Link to={graphHighlightHref(entityId)}>Open graph view</Link>
          </Button>
        </div>
      </div>
    )
  }

  const { procedure, appliesTo, parts, skills, products, site, line, breadcrumbSubject } = data
  const steps = normalizeProcedureSteps(procedure.steps)
  const additionalProperties = collectAdditionalProperties(
    'Procedure',
    procedure as unknown as Record<string, unknown>
  )
  const Icon = registryIconFor('Procedure')
  const procedureMark = markFor(procedure.id)
  const metaBits: string[] = []
  if (procedure.frequency) metaBits.push(procedure.frequency)
  if (typeof procedure.duration_minutes === 'number') {
    metaBits.push(`${procedure.duration_minutes} min`)
  }

  return (
    <div className="flex min-w-0 flex-col gap-card">
      <ProcedureFactoryBreadcrumb
        site={site}
        line={line}
        subject={breadcrumbSubject}
        currentId={procedure.id}
        currentName={procedure.name}
      />

      <header
        className={sessionDetailHeaderClass(
          'flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4',
          procedureMark
        )}
      >
        <span className="grid size-12 shrink-0 place-items-center rounded-xl bg-brand-soft text-brand">
          <Icon size={24} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="m-0 min-w-0 break-words text-2xl font-semibold text-ink">
              {procedure.name}
            </h1>
            <SessionTitleMark mark={procedureMark} />
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <Badge variant="outline">Procedure</Badge>
            {procedure.procedure_kind && (
              <Badge variant="secondary">{procedure.procedure_kind}</Badge>
            )}
            {procedure.loto_required && (
              <Badge
                variant="outline"
                className="border-amber-400/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
              >
                LOTO
              </Badge>
            )}
            <PlantStrayPill isStray={data.isStray} />
          </div>
          {metaBits.length > 0 && (
            <div className="mt-1.5 text-13 text-ink-3">{metaBits.join(' · ')}</div>
          )}
          {procedure.authored_by && (
            <div className="mt-1 text-12 text-ink-3">Authored by {procedure.authored_by}</div>
          )}
          <div className="mt-1.5 break-all font-mono text-11 text-ink-4">{procedure.id}</div>
        </div>
      </header>

      {procedure.description && (
        <p className="m-0 text-14 leading-relaxed text-ink-2">{procedure.description}</p>
      )}

      <StepsSection steps={steps} />
      <PpeSection items={procedure.ppe_required} />
      <RequirementsSection parts={parts} />
      <SkillSection skills={skills} />
      <AppliesToSection appliesTo={appliesTo} />
      <ProductsSection products={products} />
      <SourceSection procedure={procedure} />

      <EntityDetailsInspector entity={procedure} additionalProperties={additionalProperties} />
    </div>
  )
}

function ProcedureFactoryBreadcrumb({
  site,
  line,
  subject,
  currentId,
  currentName
}: {
  site?: PlantSite | null
  line?: PlantLine | null
  subject?: PlantProcedureDetail['breadcrumbSubject']
  currentId?: string
  currentName?: string
}): React.ReactElement {
  const subjectLabel = subject ? registeredLabel(subject.peerLabels) : null
  // Avoid Sites / Site / Line / Line / Procedure when PROCEDURE_FOR targets the Line.
  const showSubject = Boolean(
    subject && subjectLabel && subjectLabel !== 'Line' && subject.peer.id !== line?.id
  )

  return (
    <nav className="text-sm text-ink-3" aria-label="Breadcrumb">
      <Link to={plantEquipmentIndexHref()} className="hover:text-ink hover:underline">
        Sites
      </Link>
      {site && (
        <>
          <span className="mx-1.5 text-ink-4">/</span>
          <PlantHref
            id={site.id}
            label="Site"
            node={site}
            className="inline-flex items-center gap-1.5 hover:text-ink hover:underline"
          >
            {site.name}
            <SessionEntityMark entityId={site.id} />
          </PlantHref>
        </>
      )}
      {line && site && (
        <>
          <span className="mx-1.5 text-ink-4">/</span>
          <PlantHref
            id={line.id}
            label="Line"
            node={{ ...line, siteId: site.id }}
            className="inline-flex items-center gap-1.5 hover:text-ink hover:underline"
          >
            {line.name}
            <SessionEntityMark entityId={line.id} />
          </PlantHref>
        </>
      )}
      {line && !site && (
        <>
          <span className="mx-1.5 text-ink-4">/</span>
          <PlantHref
            id={line.id}
            label="Line"
            node={line}
            className="inline-flex items-center gap-1.5 hover:text-ink hover:underline"
          >
            {line.name}
            <SessionEntityMark entityId={line.id} />
          </PlantHref>
        </>
      )}
      {showSubject && subject && (
        <>
          <span className="mx-1.5 text-ink-4">/</span>
          <PlantHref
            id={subject.peer.id}
            label={subjectLabel ?? undefined}
            node={subject.peer}
            className="inline-flex items-center gap-1.5 hover:text-ink hover:underline"
          >
            {peerDisplayName(subject.peer, subject.peerLabels)}
            <SessionEntityMark entityId={subject.peer.id} />
          </PlantHref>
        </>
      )}
      {currentName && currentId && (
        <>
          <span className="mx-1.5 text-ink-4">/</span>
          <SessionBreadcrumbCurrent entityId={currentId}>{currentName}</SessionBreadcrumbCurrent>
        </>
      )}
    </nav>
  )
}

function StepsSection({
  steps
}: {
  steps: ReturnType<typeof normalizeProcedureSteps>
}): React.ReactElement | null {
  if (steps.length === 0) return null
  return (
    <section>
      <SectionHeader
        label="Steps"
        subtitle={`${steps.length} ${steps.length === 1 ? 'step' : 'steps'}`}
      />
      <ol className="m-0 list-none rounded-xl border bg-card p-5 text-card-foreground shadow-sm">
        {steps.map((step, index) => (
          <li
            key={`${step.stepNumber ?? index}:${step.instruction}`}
            className="flex gap-3 border-b py-3 first:pt-0 last:border-b-0 last:pb-0"
          >
            <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full bg-bg-sunk text-12 font-semibold tabular-nums text-ink-2">
              {step.stepNumber ?? index + 1}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-13 leading-snug text-ink">{step.instruction}</div>
              {step.details.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {step.details.map((detail) => (
                    <Badge key={detail.key} variant="outline" className="text-10 font-normal">
                      {detail.key}: {detail.value}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </section>
  )
}

function PpeSection({ items }: { items?: string[] }): React.ReactElement | null {
  if (!items || items.length === 0) return null
  return (
    <section>
      <SectionHeader label="PPE" subtitle={`${items.length} items`} />
      <div className="flex flex-wrap gap-1.5 rounded-xl border bg-card p-4 shadow-sm">
        {items.map((item) => (
          <Badge key={item} variant="secondary" className="text-11">
            {item}
          </Badge>
        ))}
      </div>
    </section>
  )
}

function RequirementsSection({
  parts
}: {
  parts: PlantProcedurePartRequirement[]
}): React.ReactElement | null {
  if (parts.length === 0) return null
  return (
    <section>
      <SectionHeader
        label="Requirements"
        subtitle={`${parts.length} ${parts.length === 1 ? 'part' : 'parts'}`}
      />
      <div className="flex flex-col gap-2 rounded-xl border bg-card p-4 shadow-sm">
        {parts.map(({ part, quantity, isOptional }) => (
          <div key={part.id} className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <PlantHref
              id={part.id}
              label="Part"
              node={part}
              className="text-13 font-medium text-ink no-underline hover:text-brand hover:underline"
            >
              {part.name}
            </PlantHref>
            {typeof quantity === 'number' && (
              <span className="text-12 text-ink-3">×{quantity}</span>
            )}
            {isOptional && (
              <Badge variant="outline" className="text-10">
                optional
              </Badge>
            )}
            <span className="ml-auto">
              <SessionEntityMark entityId={part.id} />
            </span>
          </div>
        ))}
      </div>
    </section>
  )
}

function SkillSection({
  skills
}: {
  skills: PlantProcedureSkillRequirement[]
}): React.ReactElement | null {
  if (skills.length === 0) return null
  return (
    <section>
      <SectionHeader
        label="Skill"
        subtitle={`${skills.length} ${skills.length === 1 ? 'role' : 'roles'}`}
      />
      <div className="flex flex-wrap gap-2 rounded-xl border bg-card p-4 shadow-sm">
        {skills.map(({ role, minGrade, maxGrade }) => {
          const grade = formatGradeRange(minGrade, maxGrade)
          return (
            <PlantHref
              key={role.id}
              id={role.id}
              label="Role"
              node={role}
              className="inline-flex items-center gap-1.5 rounded-full border bg-bg-elev px-2.5 py-1 text-12 font-medium text-ink no-underline hover:border-brand/40 hover:text-brand"
            >
              {role.name}
              {grade && <span className="text-11 font-normal text-ink-3">{grade}</span>}
              <SessionEntityMark entityId={role.id} />
            </PlantHref>
          )
        })}
      </div>
    </section>
  )
}

function AppliesToSection({
  appliesTo
}: {
  appliesTo: PlantProcedureAppliesTo[]
}): React.ReactElement | null {
  if (appliesTo.length === 0) return null
  const groups = groupAppliesTo(appliesTo)
  return (
    <section>
      <SectionHeader
        label="Applies to"
        subtitle={`${appliesTo.length} ${appliesTo.length === 1 ? 'target' : 'targets'}`}
      />
      <div className="flex flex-col gap-4 rounded-xl border bg-card p-4 shadow-sm">
        {groups.map((group) => (
          <div key={group.label ?? 'unregistered'}>
            <div className="mb-1.5 text-11 font-semibold uppercase tracking-[0.08em] text-ink-3">
              {group.label ?? 'Unregistered'}
            </div>
            <div className="flex flex-col gap-1.5">
              {group.rows.map((row) => {
                const label = registeredLabel(row.peerLabels)
                return (
                  <div key={row.peer.id} className="flex min-w-0 items-center gap-2">
                    <PlantHref
                      id={row.peer.id}
                      label={label ?? undefined}
                      node={row.peer}
                      className="min-w-0 flex-1 truncate text-13 text-ink no-underline hover:text-brand hover:underline"
                    >
                      {peerDisplayName(row.peer, row.peerLabels)}
                    </PlantHref>
                    <SessionEntityMark entityId={row.peer.id} />
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

function ProductsSection({ products }: { products: PlantProductRef[] }): React.ReactElement | null {
  if (products.length === 0) return null
  return (
    <section>
      <SectionHeader
        label="Products"
        subtitle={`${products.length} ${products.length === 1 ? 'product' : 'products'}`}
      />
      <div className="flex flex-wrap gap-2 rounded-xl border bg-card p-4 shadow-sm">
        {products.map((product) => (
          <PlantHref
            key={product.id}
            id={product.id}
            label="Product"
            node={product}
            className="inline-flex items-center rounded-full border bg-bg-elev px-2.5 py-1 text-12 font-medium text-ink no-underline hover:border-brand/40 hover:text-brand"
          >
            {product.name}
            <SessionEntityMark entityId={product.id} />
          </PlantHref>
        ))}
      </div>
    </section>
  )
}

function SourceSection({
  procedure
}: {
  procedure: PlantProcedureDetailNode
}): React.ReactElement | null {
  const { openSource, viewerProps } = useSourceDocViewer()
  const sourceFile = procedureViewerSourceFile(procedure)
  const sourcePage = procedureViewerSourcePage(procedure)
  const canOpen = Boolean(procedure.lastIngestJobId && sourceFile)

  // Structured provenance is the page-level Source and evidence section from
  // EntityDetailsInspector. This section only covers legacy source_doc_id.
  if (procedure.provenance) return null

  if (canOpen) {
    return (
      <section>
        <SectionHeader label="Source" />
        <div className="rounded-xl border bg-card p-4 shadow-sm">
          <SourceDocChip
            jobId={procedure.lastIngestJobId}
            sourceFile={sourceFile}
            sourcePage={sourcePage}
            onOpen={openSource}
          />
        </div>
        <SourceDocViewer {...viewerProps} />
      </section>
    )
  }

  if (!procedure.source_doc_id) return null
  const label =
    procedure.source_doc_page !== undefined
      ? `${procedure.source_doc_id} · p. ${procedure.source_doc_page}`
      : procedure.source_doc_id

  return (
    <section>
      <SectionHeader label="Source" />
      <div className="rounded-xl border bg-card p-4 shadow-sm">
        <span className="inline-flex items-center rounded-full border bg-bg-elev px-2.5 py-1 text-12 font-medium text-ink-2">
          {label}
        </span>
      </div>
    </section>
  )
}
